import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, { MiniMap, Controls, Background, addEdge, useNodesState, useEdgesState } from 'react-flow-renderer';

// Theme colors for white background
const theme = {
  primary: '#10a5e9',
  secondary: '#10a5e9',
  darkBg: '#ffffff',
  nodeBg: '#f8fafc',
  accentBg: '#e2e8f0',
  text: '#1e293b',
  shadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  border: '#cbd5e1'
};

// Node styles
const nodeStyles = {
  root: {
    borderRadius: 16,
    border: `3px solid ${theme.secondary}`,
    background: theme.accentBg,
    color: theme.text,
    fontWeight: 700,
    fontSize: 20,
    boxShadow: theme.shadow,
    padding: '16px 20px',
    minWidth: 180,
    textAlign: 'center'
  },
  master: {
    borderRadius: 12,
    border: `2px solid ${theme.primary}`,
    background: theme.nodeBg,
    color: theme.text,
    fontWeight: 600,
    fontSize: 16,
    boxShadow: theme.shadow,
    padding: '12px 16px',
    minWidth: 150,
    textAlign: 'center'
  },
  process: {
    borderRadius: 12,
    border: `2px solid #10b981`, // Green for process flow
    background: theme.nodeBg,
    color: theme.text,
    fontWeight: 600,
    fontSize: 16,
    boxShadow: theme.shadow,
    padding: '12px 16px',
    minWidth: 150,
    textAlign: 'center'
  },
  sales: {
    borderRadius: 12,
    border: `2px solid #8b5cf6`, // Purple for sales flow
    background: theme.nodeBg,
    color: theme.text,
    fontWeight: 600,
    fontSize: 16,
    boxShadow: theme.shadow,
    padding: '12px 16px',
    minWidth: 150,
    textAlign: 'center'
  }
};

// Initial nodes data with better spacing
const initialNodes = [
  // Masters root
  {
    id: 'masters',
    data: { label: 'Master Data' },
    position: { x: 800, y: 80 },
    style: nodeStyles.root,
    type: 'input'
  },
  
  // Master data nodes (organized in three columns for better spacing)
  // Left column - Core entities
  { id: 'supplier', data: { label: 'Supplier', path: '/supplier' }, position: { x: 200, y: 200 }, style: nodeStyles.master },
  { id: 'broker', data: { label: 'Purchase Broker', path: '/broker' }, position: { x: 200, y: 300 }, style: nodeStyles.master },
  { id: 'cha', data: { label: 'CHA', path: '/cha' }, position: { x: 200, y: 400 }, style: nodeStyles.master },
  { id: 'cma', data: { label: 'CMA', path: '/cma' }, position: { x: 200, y: 500 }, style: nodeStyles.master },
  { id: 'commodity', data: { label: 'Commodity', path: '/commodity' }, position: { x: 200, y: 600 }, style: nodeStyles.master },
  
  // Center column - Supporting entities
  { id: 'cost-component', data: { label: 'Cost Component', path: '/cost-component' }, position: { x: 800, y: 200 }, style: nodeStyles.master },
  { id: 'document-type', data: { label: 'Document Type', path: '/document-type' }, position: { x: 800, y: 300 }, style: nodeStyles.master },
  { id: 'fx-rate', data: { label: 'FX Rate', path: '/fx-rate' }, position: { x: 800, y: 400 }, style: nodeStyles.master },
  { id: 'order-status', data: { label: 'Order Status', path: '/order-status' }, position: { x: 800, y: 500 }, style: nodeStyles.master },
  { id: 'sales-channel', data: { label: 'Sales Channel', path: '/sales-channel' }, position: { x: 800, y: 600 }, style: nodeStyles.master },
  
  // Right column - Contracts and agreements
  { id: 'purchase-contract-master', data: { label: 'Purchase Contract', path: '/purchase-contract' }, position: { x: 1400, y: 200 }, style: nodeStyles.master },
  { id: 'high-seas-agreement', data: { label: 'High Seas Agreement', path: '/high-seas-agreement' }, position: { x: 1400, y: 300 }, style: nodeStyles.master },

  // Process root
  {
    id: 'process',
    data: { label: 'Import Process Flow' },
    position: { x: 800, y: 800 },
    style: nodeStyles.root,
    type: 'input'
  },
  
  // Import process steps (horizontal flow)
  { id: 'purchase-contract', data: { label: 'Purchase Contract', path: '/purchase-contract' }, position: { x: 400, y: 920 }, style: nodeStyles.process },
  { id: 'import-order', data: { label: 'Import Order', path: '/import-order' }, position: { x: 600, y: 920 }, style: nodeStyles.process },
  { id: 'bill-of-lading', data: { label: 'Bill Of Lading', path: '/bill-of-lading' }, position: { x: 800, y: 920 }, style: nodeStyles.process },
  { id: 'document', data: { label: 'Documents', path: '/document' }, position: { x: 1000, y: 920 }, style: nodeStyles.process },
  { id: 'customs-clearance', data: { label: 'Customs Clearance', path: '/customs-clearance' }, position: { x: 1200, y: 920 }, style: nodeStyles.process },
  { id: 'warehouse-entry', data: { label: 'Warehouse Entry', path: '/warehouse-entry' }, position: { x: 1400, y: 920 }, style: nodeStyles.process },
  { id: 'warehouse-transfer', data: { label: 'Warehouse Transfer', path: '/warehouse-transfer' }, position: { x: 1600, y: 920 }, style: nodeStyles.process },

  // Sales process root
  {
    id: 'sales-process',
    data: { label: 'Sales Process Flow' },
    position: { x: 800, y: 1100 },
    style: { ...nodeStyles.root, borderColor: '#8b5cf6' },
    type: 'input'
  },

  // Sales process steps
  { id: 'sales-order', data: { label: 'Sales Order', path: '/sales-order' }, position: { x: 600, y: 1220 }, style: nodeStyles.sales },
  { id: 'delivery-order', data: { label: 'Delivery Order', path: '/delivery-order' }, position: { x: 800, y: 1220 }, style: nodeStyles.sales },
  { id: 'dispatch-entry', data: { label: 'Dispatch Entry', path: '/dispatch-entry' }, position: { x: 1000, y: 1220 }, style: nodeStyles.sales },
  { id: 'payment-collection', data: { label: 'Payment Collection', path: '/payment' }, position: { x: 1200, y: 1220 }, style: nodeStyles.sales }
];

const initialEdges = [
  // Masters edges (connect to root with curved lines)
  { id: 'e-masters-supplier', source: 'masters', target: 'supplier', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-broker', source: 'masters', target: 'broker', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-cha', source: 'masters', target: 'cha', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-cma', source: 'masters', target: 'cma', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-commodity', source: 'masters', target: 'commodity', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-costcomponent', source: 'masters', target: 'cost-component', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-documenttype', source: 'masters', target: 'document-type', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-fxrate', source: 'masters', target: 'fx-rate', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-orderstatus', source: 'masters', target: 'order-status', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-saleschannel', source: 'masters', target: 'sales-channel', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-pcm', source: 'masters', target: 'purchase-contract-master', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-masters-hsa', source: 'masters', target: 'high-seas-agreement', animated: true, style: { stroke: theme.primary, strokeWidth: 2 }, type: 'smoothstep' },
  
  // Process edges (sequential horizontal flow)
  { id: 'e-process-pc', source: 'process', target: 'purchase-contract', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-pc-io', source: 'purchase-contract', target: 'import-order', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-io-bol', source: 'import-order', target: 'bill-of-lading', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-bol-doc', source: 'bill-of-lading', target: 'document', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-doc-cc', source: 'document', target: 'customs-clearance', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-cc-we', source: 'customs-clearance', target: 'warehouse-entry', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-we-wt', source: 'warehouse-entry', target: 'warehouse-transfer', animated: true, style: { stroke: '#10b981', strokeWidth: 2 }, type: 'smoothstep' },

  // Sales process edges
  { id: 'e-salesprocess-so', source: 'sales-process', target: 'sales-order', animated: true, style: { stroke: '#8b5cf6', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-so-do', source: 'sales-order', target: 'delivery-order', animated: true, style: { stroke: '#8b5cf6', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-do-de', source: 'delivery-order', target: 'dispatch-entry', animated: true, style: { stroke: '#8b5cf6', strokeWidth: 2 }, type: 'smoothstep' },
  { id: 'e-de-pc', source: 'dispatch-entry', target: 'payment-collection', animated: true, style: { stroke: '#8b5cf6', strokeWidth: 2 }, type: 'smoothstep' },

  // Cross-process connections
  { id: 'e-we-so', source: 'warehouse-entry', target: 'sales-order', animated: true, style: { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '5,5' }, type: 'smoothstep' },
  { id: 'e-pcm-pc', source: 'purchase-contract-master', target: 'purchase-contract', animated: true, style: { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '5,5' }, type: 'smoothstep' },
  { id: 'e-hsa-io', source: 'high-seas-agreement', target: 'import-order', animated: true, style: { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '5,5' }, type: 'smoothstep' }
];

export default function Roadmap() {
  const navigate = useNavigate();
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  
  const onNodeClick = useCallback((event, node) => {
    if (node.data?.path) {
      navigate(node.data.path);
    }
  }, [navigate]);

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge(params, eds));
  }, [setEdges]);

  return (
    <div style={{ 
      width: '100%', 
      height: 'calc(100vh - 4rem)', 
      background: theme.darkBg,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          style={{ background: theme.darkBg }}
          nodesDraggable={true}
          nodesConnectable={true}
          elementsSelectable={true}
          deleteKeyCode="Delete"
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
            style: { strokeWidth: 2 }
          }}
        >
          <MiniMap 
            nodeColor={(n) => {
              if (n.style?.border?.includes('#10b981')) return '#10b981';
              if (n.style?.border?.includes('#8b5cf6')) return '#8b5cf6';
              return theme.primary;
            }} 
            maskColor={`${theme.darkBg}AA`} 
            style={{ background: '#f1f5f9' }}
          />
          <Controls 
            style={{ 
              background: theme.accentBg,
              borderRadius: 6,
              overflow: 'hidden'
            }} 
          />
          <Background 
            color={theme.border} 
            gap={40} 
            variant="dots" 
            size={1}
          />
        </ReactFlow>
      </div>
    </div>
  );
}